import { LitElement, html, css, type PropertyValues } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";

import "@material/web/labs/card/outlined-card.js";
import "@material/web/progress/circular-progress.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/outlined-button.js";
import "@material/web/button/text-button.js";
import "@material/web/textfield/outlined-text-field.js";
import "@material/web/select/outlined-select.js";
import "@material/web/select/select-option.js";
import "@material/web/checkbox/checkbox.js";
import "@material/web/tabs/tabs.js";
import "@material/web/tabs/primary-tab.js";
import "@material/web/icon/icon.js";
import "@material/web/dialog/dialog.js";
import type { MdDialog } from "@material/web/dialog/dialog.js";

import "../components/app-shell";
import { api, ApiError, fetchObjectURL } from "../lib/api";
import { pb } from "../lib/pb";
import { sharedStyles } from "../styles/shared";
import { inputValue, inputChecked, errorMessage } from "../lib/dom";
import { icon } from "../lib/icons";
import { toast } from "../lib/toast";
import {
  DOC_TYPES,
  TIER_LABELS,
  type AccessTier,
  type Carrier,
  type DocType,
  type DocumentMeta,
  type DppVersion,
  type EbsiCredential,
  type FieldConfig,
  type FieldDataType,
  type FieldValue,
  type Product,
  type ProductDesignation,
  type SchemaVersion,
  type UPI,
} from "../lib/types";

type TabKey = "passport" | "identifier" | "documents";
const TAB_KEYS: TabKey[] = ["passport", "identifier", "documents"];

interface FieldEntry {
  value: unknown;
  tier: AccessTier;
}

function defaultValueFor(dataType: FieldDataType): unknown {
  switch (dataType) {
    case "number":
      return 0;
    case "boolean":
      return false;
    default:
      return "";
  }
}

function docTypeLabel(t: DocType): string {
  return DOC_TYPES.find((d) => d.value === t)?.label ?? t;
}

@customElement("product-detail-page")
export class ProductDetailPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .back-link {
        display: inline-block;
        margin-bottom: 4px;
        font-size: 0.8125rem;
        color: var(--md-sys-color-on-surface-variant);
        text-decoration: none;
      }

      .back-link:hover {
        text-decoration: underline;
      }

      .badge {
        display: inline-flex;
        align-items: center;
        padding: 2px 12px;
        border-radius: 999px;
        font-size: 0.75rem;
        font-weight: 600;
      }

      .badge--draft {
        background: var(--md-sys-color-secondary-container);
        color: var(--md-sys-color-on-secondary-container);
      }

      .badge--published {
        background: var(--md-sys-color-primary-container);
        color: var(--md-sys-color-on-primary-container);
      }

      .badge--deactivated {
        background: var(--md-sys-color-error-container);
        color: var(--md-sys-color-on-error-container);
      }

      .passport-header {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
        margin-bottom: 16px;
      }

      .tab-panel {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-top: 16px;
      }

      .fields {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .field-row {
        display: grid;
        grid-template-columns: 1fr;
        gap: 12px;
        align-items: start;
      }

      @media (min-width: 600px) {
        .field-row {
          grid-template-columns: 1fr 200px;
        }
      }

      .field-row md-outlined-text-field,
      .field-row md-outlined-select {
        width: 100%;
      }

      .checkbox-row {
        display: flex;
        align-items: center;
        gap: 8px;
        min-height: 56px;
      }

      .package-shot {
        border: 1px solid var(--md-sys-color-outline-variant);
        border-radius: 12px;
        overflow: hidden;
        max-width: 360px;
      }

      .package-shot img {
        display: block;
        width: 100%;
        aspect-ratio: 4 / 3;
        object-fit: cover;
      }

      .package-shot__empty {
        aspect-ratio: 4 / 3;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--md-sys-color-on-surface-variant);
        font-size: 0.8125rem;
        background: var(--md-sys-color-surface-container-low);
      }

      .identifier-grid {
        display: flex;
        gap: 24px;
        flex-wrap: wrap;
        align-items: flex-start;
      }

      .qr-box img {
        display: block;
        border-radius: 8px;
        border: 1px solid var(--md-sys-color-outline-variant);
        background: #fff;
      }

      .identifier-details {
        display: flex;
        flex-direction: column;
        gap: 12px;
        flex: 1;
        min-width: 240px;
      }

      .identifier-details .form-row {
        align-items: center;
      }

      /* An outstanding designation choice is a "you must act" state, not a
         failure — secondary container keeps it distinct from the error-toned
         counterfeit findings used elsewhere in the portal. */
      .warn-text {
        margin: 0 0 12px;
        padding: 12px 14px;
        border-radius: 10px;
        background: var(--md-sys-color-secondary-container);
        color: var(--md-sys-color-on-secondary-container);
        font-size: 0.875rem;
      }

      .upload-row {
        display: flex;
        gap: 12px;
        align-items: flex-end;
        flex-wrap: wrap;
      }

      .upload-row input[type="file"] {
        font: inherit;
        color: var(--md-sys-color-on-surface);
      }

      .card--flush {
        padding: 0;
        overflow: hidden;
      }

      table th,
      table td {
        padding: 12px 20px;
      }

      .dialog-form {
        display: flex;
        flex-direction: column;
        gap: 12px;
        min-width: min(360px, 80vw);
      }

      .dialog-text {
        margin: 0;
      }

      md-icon svg {
        width: 1em;
        height: 1em;
        fill: currentColor;
      }
    `,
  ];

  @property() productId = "";

  @state() private product: Product | null = null;

  @state() private uploadingPackageImage = false;
  @state() private loading = true;
  @state() private error = "";
  @state() private activeTab: TabKey = "passport";

  @state() private schema: SchemaVersion | null = null;
  @state() private dppVersion: DppVersion | null = null;
  @state() private fieldValues: Record<string, FieldEntry> = {};
  @state() private jsonText: Record<string, string> = {};
  @state() private passportError = "";
  @state() private saving = false;
  @state() private publishing = false;
  @state() private creatingDraft = false;
  @state() private deactivating = false;
  @state() private deactivateReason = "";

  @query("#publish-dialog") private publishDialog?: MdDialog;
  @query("#deactivate-dialog") private deactivateDialog?: MdDialog;

  @state() private upi: UPI | null = null;
  @state() private ebsiCred: EbsiCredential | null = null;
  // undefined = unknown (the capability probe failed); false = EBSI is not
  // wired in this deployment, which the credential card reports as coming soon
  // rather than as an onboarding step the producer has failed to take.
  @state() private ebsiConfigured?: boolean;
  @state() private designation: ProductDesignation | null = null;
  @state() private savingDesignation = false;
  @state() private carriers: Carrier[] = [];
  @state() private qrImageUrl = "";

  @state() private documents: DocumentMeta[] = [];
  @state() private documentsError = "";
  @state() private uploading = false;
  @state() private uploadDocType: DocType = "other";

  protected willUpdate(changed: PropertyValues<this>): void {
    if (changed.has("productId")) {
      void this.load();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.revokeQrImage();
  }

  private revokeQrImage(): void {
    if (this.qrImageUrl) {
      URL.revokeObjectURL(this.qrImageUrl);
      this.qrImageUrl = "";
    }
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    this.revokeQrImage();

    const productId = this.productId;
    try {
      this.product = await api.get<Product>(`/catalog/products/${productId}`);
    } catch (err) {
      this.error = errorMessage(err, "Failed to load product.");
      this.loading = false;
      return;
    }

    const [
      schema,
      dppVersion,
      upi,
      carriers,
      documents,
      ebsiCred,
      ebsiCapability,
      designation,
    ] = await Promise.allSettled([
      api.get<SchemaVersion>("/dpp/schema"),
      this.loadLatestDppVersion(productId),
      api.get<UPI>(`/identifier/products/${productId}/upi`),
      api.get<Carrier[]>(`/identifier/products/${productId}/carriers`),
      api.get<DocumentMeta[]>(`/documents/products/${productId}`),
      api.get<EbsiCredential>(`/ebsivc/products/${productId}/credential`),
      api
        .get<{ ebsiConfigured: boolean }>("/provisioning/capabilities")
        .then((c) => c.ebsiConfigured)
        .catch(() => undefined),
      api.get<ProductDesignation>(`/gi/my/designations/${productId}`),
    ]);

    this.schema = schema.status === "fulfilled" ? schema.value : null;
    this.dppVersion =
      dppVersion.status === "fulfilled" ? dppVersion.value : null;
    this.upi = upi.status === "fulfilled" ? upi.value : null;
    this.carriers = carriers.status === "fulfilled" ? carriers.value : [];
    this.documents =
      documents.status === "fulfilled" ? (documents.value ?? []) : [];
    this.ebsiCred = ebsiCred.status === "fulfilled" ? ebsiCred.value : null;
    this.ebsiConfigured =
      ebsiCapability.status === "fulfilled" ? ebsiCapability.value : undefined;
    this.designation =
      designation.status === "fulfilled" ? designation.value : null;

    this.initFieldValues();

    if (this.upi) {
      try {
        this.qrImageUrl = await fetchObjectURL(
          `/identifier/products/${productId}/qr.png`,
        );
      } catch {
        this.qrImageUrl = "";
      }
    }

    this.loading = false;
  }

  private async loadLatestDppVersion(
    productId: string,
  ): Promise<DppVersion | null> {
    const list = await pb.collection("dpp_versions").getList(1, 1, {
      filter: pb.filter("product = {:productId}", { productId }),
      sort: "-created",
    });
    if (list.items.length === 0) return null;
    return api.get<DppVersion>(`/dpp/versions/${list.items[0].id}`);
  }

  // The product's compliance class decides which schema fields apply — the
  // server enforces the same rule at PATCH time, this filter just keeps the
  // form honest (no typing into a field the save would reject).
  private regulatoryCategory(): string {
    return this.product?.RegulatoryCategory || "general";
  }

  private applicableFields() {
    const c = this.regulatoryCategory();
    return (this.schema?.Fields ?? []).filter(
      (fc) => !fc.Categories?.length || fc.Categories.includes(c),
    );
  }

  private async handleCategoryChange(value: string): Promise<void> {
    if (!this.product || value === this.regulatoryCategory()) return;
    try {
      this.product = await api.patch<Product>(
        `/catalog/products/${this.product.ID}`,
        { regulatoryCategory: value },
      );
      // The applicable field set just changed; re-seed the form for it.
      this.initFieldValues();
    } catch (err) {
      this.passportError = errorMessage(err, "Could not change the category.");
    }
  }

  private initFieldValues(): void {
    const values: Record<string, FieldEntry> = {};
    const jsonText: Record<string, string> = {};
    for (const fc of this.applicableFields()) {
      const existing = this.dppVersion?.Fields?.find(
        (f) => f.FieldKey === fc.Key,
      );
      // Pre-fill product_name from the catalog product so the required field is
      // never blank by default (it is the same name the producer already set).
      const seeded =
        fc.Key === "product_name" && this.product?.Name
          ? this.product.Name
          : defaultValueFor(fc.DataType);
      const value = existing ? existing.Value : seeded;
      values[fc.Key] = { value, tier: existing?.Tier ?? fc.DefaultTier };
      if (fc.DataType === "json") {
        jsonText[fc.Key] =
          existing && existing.Value !== null && existing.Value !== undefined
            ? JSON.stringify(existing.Value, null, 2)
            : "";
      }
    }
    this.fieldValues = values;
    this.jsonText = jsonText;
  }

  private handleFieldInput(key: string, value: unknown): void {
    this.fieldValues = {
      ...this.fieldValues,
      [key]: { ...this.fieldValues[key], value },
    };
  }

  private handleTierChange(key: string, e: Event): void {
    const tier = inputValue(e) as AccessTier;
    this.fieldValues = {
      ...this.fieldValues,
      [key]: { ...this.fieldValues[key], tier },
    };
  }

  private handleTabChange(e: Event): void {
    const index = (e.target as unknown as { activeTabIndex: number })
      .activeTabIndex;
    this.activeTab = TAB_KEYS[index] ?? "passport";
  }

  private async handleCreateDraft(): Promise<void> {
    this.creatingDraft = true;
    this.passportError = "";
    try {
      this.dppVersion = await api.post<DppVersion>(
        `/dpp/products/${this.productId}/draft`,
        undefined,
      );
      this.initFieldValues();
      toast("New draft created.");
    } catch (err) {
      this.passportError = errorMessage(err, "Failed to create draft.");
    } finally {
      this.creatingDraft = false;
    }
  }

  // Collect the current form values as an API payload; returns null (and sets
  // passportError) if any JSON field is invalid.
  private collectFields(): FieldValue[] | null {
    if (!this.schema) return null;
    try {
      return this.applicableFields().map((fc) => {
        const entry = this.fieldValues[fc.Key];
        let value = entry.value;
        if (fc.DataType === "json") {
          const text = (this.jsonText[fc.Key] ?? "").trim();
          value = text === "" ? null : JSON.parse(text);
        }
        return { FieldKey: fc.Key, Value: value, Tier: entry.tier };
      });
    } catch {
      this.passportError = "One or more JSON fields contain invalid JSON.";
      return null;
    }
  }

  private async handleSave(): Promise<void> {
    if (!this.dppVersion || !this.schema) return;
    this.passportError = "";
    const fields = this.collectFields();
    if (!fields) return;

    this.saving = true;
    try {
      this.dppVersion = await api.patch<DppVersion>(
        `/dpp/versions/${this.dppVersion.ID}`,
        { fields },
      );
      this.initFieldValues();
      toast("Draft saved.");
    } catch (err) {
      this.passportError = errorMessage(err, "Failed to save draft.");
    } finally {
      this.saving = false;
    }
  }

  private async handlePublish(): Promise<void> {
    if (!this.dppVersion || !this.schema) return;
    this.passportError = "";
    const fields = this.collectFields();
    if (!fields) return;
    this.publishing = true;
    try {
      // Persist current form values first so publish validates against what's
      // on screen — avoids a "required field empty" error if the producer
      // edited fields but didn't hit Save.
      await api.patch<DppVersion>(`/dpp/versions/${this.dppVersion.ID}`, {
        fields,
      });
      this.dppVersion = await api.post<DppVersion>(
        `/dpp/versions/${this.dppVersion.ID}/publish`,
        undefined,
      );
      try {
        this.upi = await api.get<UPI>(
          `/identifier/products/${this.productId}/upi`,
        );
        this.revokeQrImage();
        this.qrImageUrl = await fetchObjectURL(
          `/identifier/products/${this.productId}/qr.png`,
        );
      } catch {
        // identifier refresh is best-effort
      }
      try {
        // Publishing requests an EBSI credential (exactly-once); reflect it.
        this.ebsiCred = await api.get<EbsiCredential>(
          `/ebsivc/products/${this.productId}/credential`,
        );
      } catch {
        // credential refresh is best-effort
      }
      const snapshotURL = this.upi?.SnapshotURL;
      toast("Published — a public snapshot has been generated.", {
        action: snapshotURL
          ? {
              label: "View",
              onClick: () => window.open(snapshotURL, "_blank", "noopener"),
            }
          : undefined,
      });
    } catch (err) {
      this.passportError = errorMessage(err, "Failed to publish.");
    } finally {
      this.publishing = false;
    }
  }

  private async handleDeactivate(): Promise<void> {
    if (!this.dppVersion) return;
    const reason = this.deactivateReason.trim();
    this.passportError = "";
    this.deactivating = true;
    try {
      this.dppVersion = await api.post<DppVersion>(
        `/dpp/versions/${this.dppVersion.ID}/deactivate`,
        { reason },
      );
      // The public snapshot link is now gone; refresh the identifier view.
      try {
        this.upi = await api.get<UPI>(
          `/identifier/products/${this.productId}/upi`,
        );
      } catch {
        // best-effort
      }
      toast("Passport deactivated — the public page is now offline.");
    } catch (err) {
      this.passportError = errorMessage(err, "Failed to deactivate.");
    } finally {
      this.deactivating = false;
      this.deactivateReason = "";
    }
  }

  private async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      toast("Copied to clipboard.");
    } catch {
      toast("Could not copy to clipboard.", true);
    }
  }

  private async handleUpload(): Promise<void> {
    const input = this.renderRoot.querySelector<HTMLInputElement>(
      "#document-file-input",
    );
    const file = input?.files?.[0];
    if (!file) {
      this.documentsError = "Choose a file to upload.";
      return;
    }
    this.documentsError = "";
    this.uploading = true;
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", this.uploadDocType);
      const doc = await api.post<DocumentMeta>(
        `/documents/products/${this.productId}`,
        formData,
      );
      this.documents = [...this.documents, doc];
      if (input) input.value = "";
      toast(`Uploaded "${doc.Filename}".`);
    } catch (err) {
      this.documentsError = errorMessage(err, "Failed to upload document.");
    } finally {
      this.uploading = false;
    }
  }

  render() {
    return html`
      <app-shell active="/products">
        <div class="page">
          <div class="page-header">
            <div>
              <a class="back-link" href="/products">← Products</a>
              <h1>${this.product?.Name ?? "Product"}</h1>
              <p class="muted">SKU: ${this.product?.SKU || "—"}</p>
            </div>
          </div>

          ${
            this.loading
              ? html`<div class="center-loading">
                  <md-circular-progress indeterminate></md-circular-progress>
                </div>`
              : this.error
                ? html`<p class="error-text">${this.error}</p>`
                : html`
                    <md-tabs @change=${(e: Event) => this.handleTabChange(e)}>
                      <md-primary-tab ?active=${this.activeTab === "passport"}>
                        <span slot="icon">${icon("description")}</span>
                        Passport
                      </md-primary-tab>
                      <md-primary-tab
                        ?active=${this.activeTab === "identifier"}
                      >
                        <span slot="icon">${icon("link")}</span>
                        Identifier
                      </md-primary-tab>
                      <md-primary-tab ?active=${this.activeTab === "documents"}>
                        <span slot="icon">${icon("attach_file")}</span>
                        Documents
                      </md-primary-tab>
                    </md-tabs>
                    <div class="tab-panel">
                      ${
                      this.activeTab === "passport"
                        ? this.renderPassportTab()
                        : this.activeTab === "identifier"
                          ? this.renderIdentifierTab()
                          : this.renderDocumentsTab()
                    }
                    </div>
                  `
          }
        </div>

        <md-dialog id="publish-dialog">
          <div slot="headline">Publish this passport?</div>
          <div slot="content">
            Once published, this version can no longer be edited — corrections
            require a new draft. A public, immutable snapshot will be generated
            and the product's QR code will resolve to it.
          </div>
          <div slot="actions">
            <md-text-button @click=${() => this.publishDialog?.close()}>
              Cancel
            </md-text-button>
            <md-filled-button
              @click=${() => {
                this.publishDialog?.close();
                void this.handlePublish();
              }}
            >
              Publish
            </md-filled-button>
          </div>
        </md-dialog>

        <md-dialog id="deactivate-dialog">
          <div slot="headline">Deactivate this passport?</div>
          <form slot="content" method="dialog" class="dialog-form">
            <p class="dialog-text">
              The public page will be taken offline — the QR code will report
              the passport as deactivated. Intended for products that have been
              recycled, destroyed or withdrawn.
            </p>
            <md-outlined-text-field
              label="Reason (optional)"
              type="textarea"
              rows="2"
              .value=${this.deactivateReason}
              @input=${(e: Event) => (this.deactivateReason = inputValue(e))}
            ></md-outlined-text-field>
          </form>
          <div slot="actions">
            <md-text-button @click=${() => this.deactivateDialog?.close()}>
              Cancel
            </md-text-button>
            <md-filled-button
              @click=${() => {
                this.deactivateDialog?.close();
                void this.handleDeactivate();
              }}
            >
              Deactivate
            </md-filled-button>
          </div>
        </md-dialog>
      </app-shell>
    `;
  }

  private renderPassportTab() {
    if (!this.schema) {
      return html`<div class="card empty">
        Could not load the Digital Product Passport field schema.
      </div>`;
    }

    if (!this.dppVersion) {
      return html`
        <div class="card empty">
          <p>This product doesn't have a Digital Product Passport yet.</p>
          ${
            this.passportError
              ? html`<p class="error-text">${this.passportError}</p>`
              : null
          }
          <md-filled-button
            ?disabled=${this.creatingDraft}
            @click=${() => void this.handleCreateDraft()}
          >
            ${this.creatingDraft ? "Creating…" : "Create draft"}
          </md-filled-button>
        </div>
      `;
    }

    const status = this.dppVersion.Status;
    const isDraft = status === "draft";
    const isDeactivated = status === "deactivated";
    const readOnly = !isDraft; // published or deactivated → fields immutable
    const statusLabel =
      status === "published"
        ? "Published"
        : isDeactivated
          ? "Deactivated"
          : "Draft";

    return html`
      <div class="card">
        <div class="passport-header">
          <span class="badge badge--${status}">${statusLabel}</span>
          ${
            this.dppVersion.PublishedAt
              ? html`<span class="muted"
                  >Published
                  ${new Date(this.dppVersion.PublishedAt).toLocaleString()}</span
                >`
              : null
          }
          <span class="muted">Schema ${this.schema.Version}</span>
          <md-outlined-select
            label="Regulatory category"
            .value=${this.regulatoryCategory()}
            @change=${(e: Event) =>
              void this.handleCategoryChange(inputValue(e))}
          >
            <md-select-option value="general">
              <div slot="headline">General goods</div>
            </md-select-option>
            <md-select-option value="medicine">
              <div slot="headline">Medicine</div>
            </md-select-option>
          </md-outlined-select>
        </div>
        ${
          this.regulatoryCategory() === "medicine"
            ? html`<p class="muted">
                Medicine: public pages carry a prescription-only notice and stay
                within factual pack data — no use, health or treatment claims
                (drug-advertising law applies to those). Sustainability fields
                do not apply.
              </p>`
            : null
        }
        ${
          isDeactivated
            ? html`<p class="error-text">
                This passport has been
                deactivated${
                this.dppVersion.DeactivationReason
                  ? `: ${this.dppVersion.DeactivationReason}`
                  : ""
              }.
                Its public page is offline. Create a new draft to issue a
                replacement.
              </p>`
            : null
        }

        <div class="fields">
          ${this.applicableFields().map((fc) => this.renderFieldRow(fc, readOnly))}
        </div>

        ${
          this.passportError
            ? html`<p class="error-text">${this.passportError}</p>`
            : null
        }

        <div class="actions-row">
          ${
            isDraft
              ? html`
                  <md-outlined-button
                    ?disabled=${this.saving || this.publishing}
                    @click=${() => void this.handleSave()}
                  >
                    ${this.saving ? "Saving…" : "Save draft"}
                  </md-outlined-button>
                  <md-filled-button
                    ?disabled=${this.saving || this.publishing}
                    @click=${() => this.publishDialog?.show()}
                  >
                    ${this.publishing ? "Publishing…" : "Publish"}
                  </md-filled-button>
                `
              : html`
                  <md-filled-button
                    ?disabled=${this.creatingDraft}
                    @click=${() => void this.handleCreateDraft()}
                  >
                    ${
                    this.creatingDraft
                      ? "Creating…"
                      : "Create new draft for corrections"
                  }
                  </md-filled-button>
                  ${
                  status === "published"
                    ? html`<md-outlined-button
                        ?disabled=${this.deactivating}
                        @click=${() => {
                        this.deactivateReason = "";
                        this.deactivateDialog?.show();
                      }}
                      >
                        ${this.deactivating ? "Deactivating…" : "Deactivate"}
                      </md-outlined-button>`
                    : null
                }
                `
          }
        </div>
      </div>
    `;
  }

  private renderFieldRow(fc: FieldConfig, readOnly: boolean) {
    const entry = this.fieldValues[fc.Key];
    if (!entry) return null;

    return html`
      <div class="field-row">
        ${this.renderFieldInput(fc, entry, readOnly)}
        <md-outlined-select
          label="Visibility"
          .value=${entry.tier}
          ?disabled=${readOnly}
          @change=${(e: Event) => this.handleTierChange(fc.Key, e)}
        >
          ${(Object.keys(TIER_LABELS) as AccessTier[]).map(
            (t) => html`
              <md-select-option value=${t}>
                <div slot="headline">${TIER_LABELS[t]}</div>
              </md-select-option>
            `,
          )}
        </md-outlined-select>
      </div>
    `;
  }

  private renderFieldInput(
    fc: FieldConfig,
    entry: FieldEntry,
    readOnly: boolean,
  ) {
    // A required field is marked with a trailing "*" in its label so the cue is
    // consistent across every input type (Material text fields don't render the
    // asterisk themselves).
    const label = fc.Required ? `${fc.Label} *` : fc.Label;
    switch (fc.DataType) {
      case "number":
        return html`<md-outlined-text-field
          label=${label}
          type="number"
          inputmode="decimal"
          ?required=${fc.Required}
          ?disabled=${readOnly}
          .value=${String(entry.value ?? 0)}
          @input=${(e: Event) =>
            this.handleFieldInput(fc.Key, Number(inputValue(e)))}
        ></md-outlined-text-field>`;
      case "boolean":
        return html`<label class="checkbox-row">
          <md-checkbox
            ?checked=${Boolean(entry.value)}
            ?disabled=${readOnly}
            @change=${(e: Event) =>
              this.handleFieldInput(fc.Key, inputChecked(e))}
          ></md-checkbox>
          <span>${label}</span>
        </label>`;
      case "json":
        return html`<md-outlined-text-field
          label=${label}
          type="textarea"
          rows="4"
          ?required=${fc.Required}
          ?disabled=${readOnly}
          .value=${this.jsonText[fc.Key] ?? ""}
          @input=${(e: Event) => {
            this.jsonText = { ...this.jsonText, [fc.Key]: inputValue(e) };
          }}
        ></md-outlined-text-field>`;
      default:
        return html`<md-outlined-text-field
          label=${label}
          ?required=${fc.Required}
          ?disabled=${readOnly}
          .value=${String(entry.value ?? "")}
          @input=${(e: Event) => this.handleFieldInput(fc.Key, inputValue(e))}
        ></md-outlined-text-field>`;
    }
  }

  /** Upload the marketed physical package shot — the one real image the
   * public tap page shows so buyers can compare the item in hand. */
  private async onPackageImagePicked(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file || !this.product) return;
    this.uploadingPackageImage = true;
    try {
      const form = new FormData();
      form.append("image", file);
      this.product = await api.post<Product>(
        `/catalog/products/${this.product.ID}/package-image`,
        form,
      );
      toast("Package photo updated.");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Upload failed.");
    } finally {
      this.uploadingPackageImage = false;
    }
  }

  private renderPackageImageCard() {
    if (!this.product) return null;
    const url = this.product.PackageImageURL;
    return html`
      <div class="card">
        <h2>Package photo</h2>
        <p class="muted" style="margin:0 0 12px;font-size:0.8125rem">
          The marketed physical packaging, shown on the public tap page so a
          buyer can compare the item in hand with the genuine article.
        </p>
        <div class="package-shot">
          ${
            url
              ? html`<img
                  src=${url}
                  alt="Marketed package of ${this.product.Name}"
                  loading="lazy"
                />`
              : html`<div class="package-shot__empty">No photo yet</div>`
          }
        </div>
        <div class="actions-row" style="margin-top:12px">
          <md-outlined-button
            ?disabled=${this.uploadingPackageImage}
            @click=${() =>
              this.renderRoot
                .querySelector<HTMLInputElement>("#package-image-input")
                ?.click()}
          >
            ${
              this.uploadingPackageImage
                ? "Uploading…"
                : url
                  ? "Replace photo"
                  : "Upload photo"
            }
          </md-outlined-button>
          <input
            id="package-image-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            @change=${(e: Event) => void this.onPackageImagePicked(e)}
          />
        </div>
      </div>
    `;
  }

  private renderIdentifierTab() {
    if (!this.upi) {
      return html`<div class="card empty">
        The product identifier hasn't been provisioned yet. This usually happens
        automatically right after a product is created.
      </div>`;
    }

    return html`
      ${this.renderPackageImageCard()}
      <div class="card">
        <h2>GS1 Digital Link</h2>
        <div class="identifier-grid">
          <div class="qr-box">
            ${
              this.qrImageUrl
                ? html`<img
                    src=${this.qrImageUrl}
                    width="220"
                    height="220"
                    alt="QR code linking to the public passport"
                  />`
                : html`<md-circular-progress
                    indeterminate
                  ></md-circular-progress>`
            }
          </div>
          <div class="identifier-details">
            <div class="form-row">
              <span class="muted">UPI code</span>
              <code>${this.upi.Code}</code>
            </div>
            <div class="form-row">
              <span class="muted">Public link</span>
              <a href=${this.upi.GS1URL} target="_blank" rel="noopener"
                >${this.upi.GS1URL}</a
              >
              <md-text-button
                @click=${() => void this.copyToClipboard(this.upi!.GS1URL)}
              >
                Copy
              </md-text-button>
            </div>
            ${
              this.upi.SnapshotURL
                ? html`<div class="form-row">
                    <span class="muted">Public passport</span>
                    <a
                      href=${this.upi.SnapshotURL}
                      target="_blank"
                      rel="noopener"
                      >${this.upi.SnapshotURL}</a
                    >
                  </div>`
                : html`<p class="muted">
                    Publish the passport to generate a public snapshot.
                  </p>`
            }
          </div>
        </div>
      </div>

      ${this.renderDesignationCard()} ${this.renderEbsiCredentialCard()}
      ${
        this.carriers.length > 0
          ? html`
              <div class="card card--flush">
                <div class="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Carrier</th>
                        <th>Payload</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${this.carriers.map(
                    (c) => html`
                      <tr>
                        <td>${c.CarrierType}</td>
                        <td><code>${c.Payload}</code></td>
                        <td>${new Date(c.CreatedAt).toLocaleDateString()}</td>
                      </tr>
                    `,
                  )}
                    </tbody>
                  </table>
                </div>
              </div>
            `
          : null
      }
    `;
  }

  // Which protected designation this product is sold under. A licence belongs
  // to the organization, but a passport is issued per product — so an org
  // holding several designations has to say which one covers this product.
  // Until it does, the public passport and tap page make NO origin claim,
  // because guessing would print the wrong designation on an immutable page.
  private renderDesignationCard() {
    const d = this.designation;
    // Orgs with no GI licence at all never see this card.
    if (!d || d.options.length === 0) return null;

    // A single licence needs no decision — state it and stop.
    if (d.options.length === 1 && !d.giProducerId) {
      return html`
        <div class="card">
          <h2>Protected designation</h2>
          <div class="form-row">
            <span class="muted">Sold under</span>
            <span>${d.giName}</span>
          </div>
          <p class="muted">
            Your organization holds one designation, so it applies to every
            product automatically.
          </p>
        </div>
      `;
    }

    return html`
      <div class="card">
        <h2>Protected designation</h2>
        ${
          d.ambiguous
            ? html`
                <p class="warn-text">
                  Your organization holds ${d.options.length} designations, so
                  we cannot tell which one covers this product. Until you
                  choose, the public passport and the tap page show no
                  designation at all — naming the wrong one would be printed
                  onto a page that cannot be edited after publishing.
                </p>
              `
            : html`
                <div class="form-row">
                  <span class="muted">Sold under</span>
                  <span>${d.giName}</span>
                </div>
              `
        }
        <md-outlined-select
          label="Designation for this product"
          ?disabled=${this.savingDesignation}
          @change=${(e: Event) =>
            void this.handleDesignationChange(
              (e.target as HTMLSelectElement).value,
            )}
        >
          <md-select-option value="" ?selected=${d.giProducerId === ""}>
            <div slot="headline">Not stated</div>
          </md-select-option>
          ${d.options.map(
            (o) => html`
              <md-select-option
                value=${o.giProducerId}
                ?selected=${o.giProducerId === d.giProducerId}
              >
                <div slot="headline">${o.giName}</div>
                <div slot="supporting-text">
                  Licence ${o.licenseNumber}${o.region ? ` — ${o.region}` : ""}
                </div>
              </md-select-option>
            `,
          )}
        </md-outlined-select>
        <p class="muted">
          Applies to future publications. Passports already published keep the
          designation they were published with.
        </p>
      </div>
    `;
  }

  private async handleDesignationChange(giProducerId: string): Promise<void> {
    const productId = this.productId;
    if (!productId) return;
    this.savingDesignation = true;
    try {
      this.designation = await api.put<ProductDesignation>(
        `/gi/my/designations/${productId}`,
        { giProducerId },
      );
      toast(
        this.designation.resolved
          ? `Designation set to ${this.designation.giName}.`
          : "Designation cleared — this product will show no origin claim.",
      );
    } catch (err) {
      toast(errorMessage(err, "Failed to set the designation."), true);
    } finally {
      this.savingDesignation = false;
    }
  }

  // EBSI credential status for this product: issued on publish (contract
  // §3.3.3), verified at consumer tap time. "none" also covers tenants that
  // haven't completed EBSI onboarding.
  private renderEbsiCredentialCard() {
    const cred = this.ebsiCred;
    const day = (iso?: string) =>
      iso ? new Date(iso).toLocaleDateString() : "";

    if (!cred || cred.status === "none") {
      // Two different "no credential" situations, and telling them apart
      // matters: one asks the producer to finish onboarding, the other is our
      // roadmap. Sending someone to complete an onboarding that does not exist
      // yet wastes their time and reads as a broken product.
      const notBuiltYet = this.ebsiConfigured === false;
      return html`
        <div class="card">
          <h2>
            EBSI credential
            ${
              notBuiltYet
                ? html`<span class="badge badge--neutral">Coming soon</span>`
                : null
            }
          </h2>
          <p class="muted">
            ${
              notBuiltYet
                ? html`Verifiable credentials on the EU Blockchain are being
                  prepared. When they go live, publishing a passport will
                  request one automatically — nothing here needs changing.`
                : html`No credential yet. One is requested automatically when
                  the passport is published — this needs your organization's
                  EBSI onboarding (decentralized identifier) to be complete.`
            }
          </p>
        </div>
      `;
    }

    const badge =
      cred.status === "issued"
        ? html`<span class="badge badge--published">Issued</span>`
        : cred.status === "failed"
          ? html`<span class="badge badge--deactivated">Issuance failed</span>`
          : html`<span class="badge badge--deactivated">Revoked</span>`;

    return html`
      <div class="card">
        <h2>EBSI credential ${badge}</h2>
        ${
          cred.statusReason
            ? html`<p class="muted">${cred.statusReason}</p>`
            : null
        }
        <div class="identifier-details">
          ${
            cred.credentialId
              ? html`<div class="form-row">
                  <span class="muted">Credential</span>
                  <code>${cred.credentialId}</code>
                </div>`
              : null
          }
          ${
            cred.subjectDid
              ? html`<div class="form-row">
                  <span class="muted">Producer DID</span>
                  <code>${cred.subjectDid}</code>
                </div>`
              : null
          }
          ${
            cred.issuedAt
              ? html`<div class="form-row">
                  <span class="muted">Issued</span>
                  <span>${day(cred.issuedAt)}</span>
                </div>`
              : null
          }
          ${
            cred.expiresAt
              ? html`<div class="form-row">
                  <span class="muted">Expires</span>
                  <span>${day(cred.expiresAt)}</span>
                </div>`
              : null
          }
          ${
            cred.verifiedAt
              ? html`<div class="form-row">
                  <span class="muted">Last verified</span>
                  <span>
                    ${day(cred.verifiedAt)} —
                    ${cred.verifyValid ? "valid" : "failed verification"}
                  </span>
                </div>`
              : null
          }
        </div>
      </div>
    `;
  }

  private renderDocumentsTab() {
    return html`
      <div class="card">
        <h2>Upload a document</h2>
        <div class="upload-row">
          <input type="file" id="document-file-input" />
          <md-outlined-select
            label="Document type"
            .value=${this.uploadDocType}
            @change=${(e: Event) =>
              (this.uploadDocType = inputValue(e) as DocType)}
          >
            ${DOC_TYPES.map(
              (t) => html`
                <md-select-option value=${t.value}>
                  <div slot="headline">${t.label}</div>
                </md-select-option>
              `,
            )}
          </md-outlined-select>
          <md-filled-button
            ?disabled=${this.uploading}
            @click=${() => void this.handleUpload()}
          >
            ${this.uploading ? "Uploading…" : "Upload"}
          </md-filled-button>
        </div>
        ${
          this.documentsError
            ? html`<p class="error-text">${this.documentsError}</p>`
            : null
        }
      </div>

      <div class="card card--flush">
        ${
          this.documents.length === 0
            ? html`<div class="empty">No documents uploaded yet.</div>`
            : html`
                <div class="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>File</th>
                        <th>Type</th>
                        <th>Uploaded</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      ${this.documents.map(
                    (d) => html`
                      <tr>
                        <td>${d.Filename}</td>
                        <td>${docTypeLabel(d.DocType)}</td>
                        <td>${new Date(d.UploadedAt).toLocaleString()}</td>
                        <td>
                          <a href=${d.PublicURL} target="_blank" rel="noopener"
                            >Download</a
                          >
                        </td>
                      </tr>
                    `,
                  )}
                    </tbody>
                  </table>
                </div>
              `
        }
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "product-detail-page": ProductDetailPage;
  }
}
